import type { GameContext } from '../core/context';
import type { V2 } from '../core/math';
import type { System } from '../core/system';
import { touchUi } from '../input/touch';
import type { OnlineGame } from '../online-ui/game';
import { hideoutState } from '../online-ui/hideoutUi';
import { sceneOverride } from '../render/sceneOverride';
import { BackRoomVisit, type VisitPhase } from '../weapons/backRoom/visit';
import { HIDEOUT_ITEMS, hideoutItem, type HideoutItemId, type JukeboxStation } from '../../shared/hideout';
import type { HideoutView } from '../../shared/messages/hideout';
import { itemCards } from './hideoutFormat';
import { HideoutPanel, type HideoutPanelState } from './hideoutPanel';
import { HideoutScene } from './hideoutScene';

/**
 * The hideout (order 716; WP-X4, docs/HIDEOUT.md): online, a gang member at the door of 9 Pilcher Street (the
 * safehouse) presses E and goes in — a fade to black, then the gang's own back parlour drawn INSTEAD of the city
 * (render/sceneOverride.ts, the approach of Sid's back room: the same door state machine, visit.ts). The room is
 * dressed with what the gang has bought and the members inside; the panel buys furnishings from the treasury and
 * sets the jukebox (which plays that radio station while you're in). Every decision is the server's
 * (`hideoutEnter` / `hideoutBuy` / `hideoutStation`); the room only shows its `hideout` view. ✕ / Esc / E goes out.
 */

export interface HideoutInfo {
  phase: VisitPhase;
  roomUp: boolean;
  /** The server has us in the room. */
  inside: boolean;
  /** Furnishings drawn in the room, figures in it, the jukebox's station. */
  shown: string[];
  figures: number;
  station: JukeboxStation | null;
  note: string;
  /** The hideout's door (null: no safehouse in this layout). */
  door: { x: number; z: number } | null;
}

declare module '../core/hooks' {
  interface BhHooks {
    /** The hideout: where the visit is, what the room shows (JSON). */
    hideoutState(): HideoutInfo;
    /** Where a furnishing's BUY button is on screen (CSS px), for a real tap; null if it isn't there. */
    hideoutScreenPos(item: string): { x: number; y: number } | null;
    /** Debug / screenshots: show the fully furnished room over the canvas (true), or put it away (false). */
    hideoutPreview(on: boolean): boolean;
  }
}

/** How close to the door spot E reaches (m), and how far it sits along the frontage from the safehouse's own spot. */
const DOOR_REACH = 1.8;
const DOOR_ALONG = 2.6;

export function createHideoutSystem(): System {
  let ctx: GameContext;
  const visit = new BackRoomVisit();
  let door: V2 | null = null;
  let panel: HideoutPanel | null = null;
  let room: HideoutScene | null = null;
  /** `__bh.hideoutPreview(true)`: the furnished room without a visit (debug, screenshots). */
  let preview: HideoutScene | null = null;
  /** The server answered our `hideoutEnter`: we're in its room (a later view saying otherwise sends us out). */
  let confirmed = false;
  let focus = 0;
  let note = '';
  let noteBad = false;
  let busy = false;
  let viewKey = '';
  let playing: JukeboxStation | null = null;
  const offs: (() => void)[] = [];

  const game = (): OnlineGame | undefined => ctx.services.tryGet('online');
  const online = (): boolean => ctx.services.tryGet('economy')?.authority === 'server' && !!game()?.active;
  const inGang = (): boolean => !!game()?.store.state.gang;
  const view = (): HideoutView | null => hideoutState.view;

  function stationNames(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const s of ctx.services.tryGet('audio')?.radio.stations ?? []) out[s.id] = s.name;
    return out;
  }

  function state(): HideoutPanelState {
    return { touch: touchUi.enabled, view: view(), station: view()?.station ?? null, stationNames: stationNames(), focus, note, noteBad, busy };
  }

  function refresh(): void {
    if (panel?.roomShown) panel.render(state());
  }

  /** The room, the jukebox and the panel follow the server's view. */
  function apply(v: HideoutView | null): void {
    if (!visit.busy) return;
    if (!v) {
      // Out of the gang while inside: out of the room.
      leave();
      return;
    }
    if (confirmed && !v.you.inside && visit.inside) {
      note = 'You’re out of the room.';
      leave();
      return;
    }
    if (room) room.setGang(v, Date.now());
    const want = visit.inside && v.items.some((i) => i.id === 'jukebox') ? v.station : null;
    if (want !== playing) {
      playing = want;
      ctx.services.tryGet('audio')?.jukebox(want);
    }
    refresh();
  }

  // ------------------------------------------------------------------ buying, the dial

  async function buy(item: HideoutItemId): Promise<void> {
    const g = game();
    if (!g || busy || !visit.inside) return;
    const def = hideoutItem(item);
    if (!def) return;
    busy = true;
    note = '';
    refresh();
    const r = await g.request<{ hideout: HideoutView | null; replayed?: boolean }>('hideoutBuy', { item }, { quiet: true });
    busy = false;
    const audio = ctx.services.tryGet('audio');
    if (r.ok) {
      audio?.ui('cash');
      audio?.ui('stamp');
      noteBad = false;
      note = `${def.name} — bought from the treasury. It’s in the hideout for good.`;
      panel?.celebrate('Bought');
      if (r.data?.hideout) hideoutState.view = r.data.hideout;
      apply(view());
    } else {
      audio?.ui('denied');
      noteBad = true;
      note = g.actions.explain(r.error!);
      panel?.celebrate('Not bought', true);
    }
    refresh();
  }

  async function setStation(s: JukeboxStation | null): Promise<void> {
    const g = game();
    if (!g || busy || !visit.inside) return;
    busy = true;
    refresh();
    const r = await g.request<{ hideout: HideoutView | null }>('hideoutStation', s ? { station: s } : {}, { quiet: true });
    busy = false;
    if (r.ok) {
      ctx.services.tryGet('audio')?.ui('tick');
      noteBad = false;
      note = s ? `The jukebox is on ${stationNames()[s] ?? s}. Everyone in hears it.` : 'The jukebox is off.';
      if (r.data?.hideout) hideoutState.view = r.data.hideout;
      apply(view());
    } else {
      noteBad = true;
      note = g.actions.explain(r.error!);
    }
    refresh();
  }

  // ------------------------------------------------------------------ in and out

  function begin(): void {
    const g = game();
    if (!g || !online()) return;
    if (!inGang()) {
      ctx.services.tryGet('ui')?.toast('The hideout is a gang’s. Join one, or start one in the gang ledger.', 'warn');
      return;
    }
    if (!panel) return;
    const uiSvc = ctx.services.tryGet('ui');
    if (uiSvc && !uiSvc.openOverlay(panel, 'hideout', () => abort())) return;
    if (!visit.enter()) return;
    confirmed = false;
    focus = 0;
    note = '';
    noteBad = false;
    panel.setOpen(true);
    panel.setFade(0);
    ctx.uiRoot?.classList.add('hd-open');
    ctx.services.tryGet('player')?.controls.acquire('hideout');
    ctx.input.clearTouch?.();
    ctx.services.tryGet('audio')?.ui('whoosh');
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('keyup', swallow, true);
    void g.request<{ hideout: HideoutView | null }>('hideoutEnter', { inside: true }, { quiet: true }).then((r) => {
      if (!visit.busy) return;
      if (!r.ok) {
        ctx.services.tryGet('ui')?.toast(g.actions.explain(r.error!), 'warn');
        ctx.services.tryGet('audio')?.ui('denied');
        leave();
        return;
      }
      confirmed = true;
      if (r.data?.hideout) hideoutState.view = r.data.hideout;
      apply(view());
    });
  }

  function leave(): void {
    if (visit.leave()) ctx.services.tryGet('audio')?.ui('back');
  }

  function putRoomUp(): void {
    if (!panel || !ctx.render) return;
    const tier = ctx.services.tryGet('renderPipeline')?.quality ?? 'low';
    room = new HideoutScene(tier !== 'low');
    sceneOverride.scene = room.scene;
    sceneOverride.camera = room.camera;
    viewKey = '';
    panel.setRoom(true);
    const v = view();
    if (v) room.setGang(v, Date.now());
    frame();
    refresh();
  }

  function takeRoomDown(): void {
    if (room && sceneOverride.scene === room.scene) {
      sceneOverride.scene = null;
      sceneOverride.camera = null;
    }
    room?.dispose();
    room = null;
    panel?.setRoom(false);
  }

  function finish(closeOverlay: boolean): void {
    takeRoomDown();
    if (closeOverlay && panel) ctx.services.tryGet('ui')?.closeOverlay(panel);
    panel?.setOpen(false);
    ctx.uiRoot?.classList.remove('hd-open');
    window.removeEventListener('keydown', onKey, true);
    window.removeEventListener('keyup', swallow, true);
    ctx.services.tryGet('player')?.controls.drop('hideout');
    if (typeof window !== 'undefined') window.dispatchEvent(new Event('blur'));
    if (playing !== null) ctx.services.tryGet('audio')?.jukebox(null);
    playing = null;
    busy = false;
    if (confirmed) void game()?.request('hideoutEnter', { inside: false }, { quiet: true });
    confirmed = false;
  }

  function abort(): void {
    if (!visit.busy) return;
    visit.reset();
    finish(false);
  }

  function bail(): void {
    if (!visit.busy) return;
    visit.reset();
    finish(true);
  }

  function frame(): void {
    if (!room || !panel || !ctx.render) return;
    const c = ctx.render.canvas.getBoundingClientRect();
    const s = panel.stage.getBoundingClientRect();
    const key = `${c.width}|${c.height}|${s.left}|${s.top}|${s.width}|${s.height}`;
    if (key === viewKey) return;
    viewKey = key;
    room.setView(c.width, c.height, { x: s.left - c.left, y: s.top - c.top, w: s.width, h: s.height });
  }

  const swallow = (e: KeyboardEvent): void => {
    e.stopImmediatePropagation();
  };

  function onKey(e: KeyboardEvent): void {
    e.stopImmediatePropagation();
    if (e.repeat || !visit.inside) return;
    const c = e.code;
    if (c === 'Escape' || c === 'Backspace' || c === 'KeyE' || c === 'KeyB' || c === 'KeyF') {
      e.preventDefault();
      leave();
    } else if (c === 'ArrowUp' || c === 'KeyW') moveFocus(-1);
    else if (c === 'ArrowDown' || c === 'KeyS') moveFocus(1);
    else if (c === 'Enter' || c === 'Space') {
      e.preventDefault();
      const v = view();
      const card = v ? itemCards(v)[focus] : undefined;
      if (card?.action?.enabled) void buy(card.def.id);
    }
  }

  function moveFocus(d: number): void {
    const v = view();
    const n = v ? itemCards(v).length : 0;
    focus = Math.max(0, Math.min(n - 1, focus + d));
    refresh();
  }

  function info(): HideoutInfo {
    return { phase: visit.phase, roomUp: !!room, inside: confirmed, shown: room?.shown ?? [], figures: room?.figures ?? 0, station: playing, note, door: door ? { x: door.x, z: door.z } : null };
  }

  return {
    id: 'hideout',
    order: 716,
    init(c) {
      ctx = c;
      c.hooks.register('hideoutState', () => info());
      // Debug / screenshots (offline too): the room fully furnished over the whole canvas, or put away.
      c.hooks.register('hideoutPreview', (on) => {
        preview?.dispose();
        if (preview && sceneOverride.scene === preview.scene) sceneOverride.scene = sceneOverride.camera = null;
        preview = null;
        if (!on || !c.render || room) return false;
        preview = new HideoutScene((c.services.tryGet('renderPipeline')?.quality ?? 'low') !== 'low');
        const now = Date.now();
        const items = HIDEOUT_ITEMS.map((i) => ({ id: i.id, boughtAt: now, by: 'Ada' }));
        const members = ['Ada', 'Bert', 'Cyril'];
        preview.setGang({ gangId: 1, name: 'Pilcher Lot', tag: 'PIL', color: 0xc0392b, color2: 0x2c3e50, emblem: 'crow', createdAt: now, items, station: 1, inside: [], members, trophies: [], history: [], freightRuns: 0, treasury: 0, you: { cid: 1, furnish: true, inside: true } } as unknown as HideoutView, now);
        const box = c.render.canvas.getBoundingClientRect();
        preview.setView(box.width, box.height, { x: 0, y: 0, w: box.width, h: box.height });
        sceneOverride.scene = preview.scene;
        sceneOverride.camera = preview.camera;
        return true;
      });
      c.hooks.register('hideoutScreenPos', (item) => panel?.buttonCenter(item) ?? null);
    },
    start() {
      const poi = ctx.services.tryGet('layout')?.pois.find((p) => p.kind === 'safehouse');
      const interactions = ctx.services.tryGet('interactions');
      if (!poi || !interactions) return;
      // The hideout's own door, a little along the frontage from the safehouse's spot (the rumour board stays there).
      const h = poi.heading ?? 0;
      door = { x: poi.pos.x + Math.cos(h) * DOOR_ALONG, z: poi.pos.z - Math.sin(h) * DOOR_ALONG };
      const d = door;
      if (ctx.uiRoot && typeof document !== 'undefined' && ctx.render) {
        panel = new HideoutPanel(ctx.uiRoot, {
          buy: (item) => void buy(item),
          station: (s) => void setStation(s),
          focus: (i) => {
            focus = i;
            refresh();
          },
          leave,
        });
      }
      offs.push(
        interactions.register({
          id: 'hideout:door',
          pos: () => d,
          radius: DOOR_REACH,
          priority: 2,
          enabled: () => !visit.busy && online() && inGang(),
          prompt: () => `Go in: the hideout (${game()?.store.state.gang?.tag ?? 'your gang'})`,
          onInteract: () => begin(),
        }),
        hideoutState.listen((v) => apply(v)),
        ctx.events.on('playerWasted', () => bail()),
        ctx.events.on('playerBusted', () => bail()),
      );
    },
    fixedUpdate() {
      if (!visit.inside) return;
      if (ctx.input.pressed('enterExit')) leave();
    },
    update(dt) {
      if (!visit.busy) return;
      const outcome = ctx.services.tryGet('police')?.outcome ?? 'none';
      const p = ctx.services.tryGet('player');
      if (!online() || outcome !== 'none' || ctx.state.mode !== 'playing' || (p && p.mode !== 'foot')) {
        bail();
        return;
      }
      if (visit.inside && door && p && Math.hypot(p.pos.x - door.x, p.pos.z - door.z) > DOOR_REACH + 3) leave();
      const ev = visit.update(dt);
      if (ev === 'swapIn') putRoomUp();
      else if (ev === 'swapOut') takeRoomDown();
      else if (ev === 'out') {
        finish(true);
        return;
      }
      panel?.setFade(visit.black);
      if (room) {
        frame();
        room.update(dt, playing !== null);
      }
      preview?.update(dt, true);
    },
    dispose() {
      bail();
      for (const off of offs) off();
      offs.length = 0;
      panel?.dispose();
      panel = null;
    },
  };
}
